import type { AxiosError } from 'axios';
import type { ethers } from 'ethers';
import type { SWRConfiguration } from 'swr';
import useSWR from 'swr';

import useProvider from 'src/hooks/useProivder';
import type { EthereumTx } from 'src/types/tx';
import { times, toDisplayDenomAmount } from 'src/utils/number';

type FetchParams = {
  tx: EthereumTx;
  provider: ethers.BrowserProvider;
};

type UseEstimatedFeeProps = {
  tx?: EthereumTx;
};

export default function useEstimatedFee({ tx }: UseEstimatedFeeProps, config?: SWRConfiguration) {
  const { provider } = useProvider();

  const fetcher = async (params: FetchParams) => {
    const gasLimit = await params.provider.estimateGas(params.tx);
    const baseGasLimit = gasLimit.toString();

    const feeData = await params.provider.getFeeData();

    const baseEstimatedFeeAmount = times(baseGasLimit || '0', feeData.gasPrice?.toString() || '0');

    const displayEstimatedFeeAmount = toDisplayDenomAmount(baseEstimatedFeeAmount, 18);

    return displayEstimatedFeeAmount;
  };

  const { data, isValidating, error, mutate } = useSWR<string, AxiosError>({ tx, provider }, fetcher, {
    dedupingInterval: 3000,
    refreshInterval: 4000,
    errorRetryCount: 0,
    isPaused: () => !tx || !provider,
    ...config,
  });

  return { data, isValidating, error, mutate };
}
